import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2, Trash2 } from 'lucide-react'
import { usePatchDocumentContext } from '@/api/queries'
import {
  MAX_CONTEXT_CHARS,
  type DocumentDetail,
} from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'

/**
 * Review / edit / clear the per-document initial-context snapshot.
 *
 * The snapshot is taken at upload time from the reusable workspace context
 * (or an upload-time override) and is what the autofill agent draws on to
 * pre-populate fields. Showing it back here gives the user visible state
 * that the context is being applied — and lets them edit or clear it
 * without affecting the reusable blob or any other docs.
 *
 * Wire features added for the prompts/instructions/context UI task:
 *  - Token-budget bar against ``MAX_CONTEXT_CHARS`` so the user can see
 *    how close they are to the per-slice cap.
 *  - Destructive confirmation step before Clear (the panel won't ship the
 *    DELETE / null patch until the user acknowledges).
 *  - Optimistic UI handled inside ``usePatchDocumentContext``: the cache
 *    flips immediately and the mutation rolls back on failure (the toast
 *    error message tells the user the write didn't land).
 *  - ``readOnly`` mode for users without write permission on the doc.
 */
export function DocumentContextPanel({
  doc,
  readOnly = false,
}: {
  doc: DocumentDetail
  readOnly?: boolean
}) {
  const patch = usePatchDocumentContext(doc.uuid)
  const initial = doc.initial_context ?? ''
  const [draft, setDraft] = useState<string | null>(null)
  const [confirmingClear, setConfirmingClear] = useState(false)
  const editing = draft !== null

  const charCount = (draft ?? '').length
  const tooLong = charCount > MAX_CONTEXT_CHARS
  const dirty = draft !== null && draft !== initial
  const hasContext = Boolean(initial)
  const percent = Math.min(
    100,
    Math.round((charCount / MAX_CONTEXT_CHARS) * 100),
  )
  const tone: 'primary' | 'warning' | 'danger' = tooLong
    ? 'danger'
    : percent >= 80
      ? 'warning'
      : 'primary'

  const onSave = async () => {
    if (readOnly) return
    const trimmed = (draft ?? '').trim()
    if (trimmed.length > MAX_CONTEXT_CHARS) {
      toast.error(
        `Trim to ${MAX_CONTEXT_CHARS.toLocaleString()} characters before saving`,
      )
      return
    }
    try {
      await patch.mutateAsync({ initial_context: trimmed || null })
      toast.success(
        trimmed ? 'Updated document context' : 'Cleared document context',
      )
      setDraft(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed')
    }
  }

  const onClearAsk = () => {
    if (readOnly || !hasContext) return
    setConfirmingClear(true)
  }

  const onClearConfirm = async () => {
    setConfirmingClear(false)
    try {
      await patch.mutateAsync({ initial_context: null })
      toast.success('Cleared document context')
      setDraft(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Clear failed')
    }
  }

  if (!editing) {
    return (
      <section
        data-testid="document-context-panel"
        data-readonly={readOnly || undefined}
        aria-labelledby="document-context-heading"
        className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
      >
        <div className="flex items-center justify-between gap-2">
          <h3 id="document-context-heading" className="aurora-eyebrow">
            Initial context
          </h3>
          <div className="flex items-center gap-2">
            {readOnly && (
              <Badge
                tone="neutral"
                size="xs"
                data-testid="document-context-readonly"
              >
                Read-only
              </Badge>
            )}
            {hasContext && (
              <Badge
                tone="success"
                size="xs"
                data-testid="document-context-applied-badge"
              >
                Applied
              </Badge>
            )}
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDraft(initial)}
                data-testid="document-context-edit"
              >
                {hasContext ? 'Edit' : 'Add context'}
              </Button>
            )}
          </div>
        </div>
        {hasContext ? (
          <pre
            data-testid="document-context-content"
            className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-card p-2 font-sans text-xs leading-relaxed text-foreground/90"
          >
            {initial}
          </pre>
        ) : (
          <p className="text-xs leading-relaxed text-muted-foreground">
            No initial context applied to this document. The agent will rely
            only on the source document + your chat instructions.
          </p>
        )}
      </section>
    )
  }

  return (
    <section
      data-testid="document-context-panel"
      aria-labelledby="document-context-heading"
      className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 id="document-context-heading" className="aurora-eyebrow">
          Initial context
        </h3>
        {hasContext && !confirmingClear && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAsk}
            disabled={patch.isPending}
            data-testid="document-context-clear"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="size-3.5" aria-hidden />
            Clear
          </Button>
        )}
      </div>
      <Label htmlFor={`doc-context-${doc.uuid}`} className="sr-only">
        Initial context
      </Label>
      <Textarea
        id={`doc-context-${doc.uuid}`}
        data-testid="document-context-textarea"
        value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value)}
        rows={6}
        placeholder="Optional context for this document only."
        className="resize-y text-xs"
        disabled={patch.isPending}
        aria-invalid={tooLong || undefined}
      />
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
          <span
            data-testid="document-context-charcount"
            className={tooLong ? 'text-destructive' : undefined}
          >
            {charCount.toLocaleString()} / {MAX_CONTEXT_CHARS.toLocaleString()}
          </span>
          {tooLong && (
            <Badge tone="danger" size="xs">
              Too long
            </Badge>
          )}
        </div>
        <Progress
          percent={percent}
          size="sm"
          tone={tone}
          data-testid="document-context-budget-bar"
        />
      </div>

      {confirmingClear ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive-soft/40 px-3 py-2 text-xs text-destructive-soft-foreground"
          role="alertdialog"
          aria-label="Confirm clear initial context"
          data-testid="document-context-clear-confirm"
        >
          <span className="font-medium">
            Clear the initial context? This cannot be undone.
          </span>
          <div className="ml-auto flex gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmingClear(false)}
              disabled={patch.isPending}
              data-testid="document-context-clear-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onClearConfirm}
              disabled={patch.isPending}
              data-testid="document-context-clear-confirm-button"
            >
              {patch.isPending && <Loader2 className="size-3 animate-spin" />}
              Clear
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setDraft(null)
              setConfirmingClear(false)
            }}
            disabled={patch.isPending}
            data-testid="document-context-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onSave}
            disabled={!dirty || tooLong || patch.isPending}
            data-testid="document-context-save"
          >
            {patch.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Save
          </Button>
        </div>
      )}
    </section>
  )
}
